import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Zap, Plus, X, Clock, TrendingDown, TrendingUp, AlertTriangle, Sparkles, RefreshCw, Check, CreditCard, Loader2, ReceiptText } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/portal/AuthContext";

const eur = (n) => `${Number(n || 0).toLocaleString("de-DE")} €`;
const KIND_ICON = { topup: TrendingUp, migration: Sparkles, package_reset: RefreshCw, consume: TrendingDown, refund: TrendingUp };

/** Globale Credit-Punkte-Wallet (Anzeige + Panel) inkl. echter Stripe-Aufladung, Auto-Aufladung und Verlauf. */
export const CreditWallet = ({ className = "", align = "right", dark = false }) => {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [enabled, setEnabled] = useState(null);
  const [open, setOpen] = useState(false);
  const [packages, setPackages] = useState(null);
  const [buying, setBuying] = useState(null);
  const [arConsent, setArConsent] = useState(false);
  const [savingAR, setSavingAR] = useState(false);
  const [receipt, setReceipt] = useState(null); // Nachkauf-Beleg nach Stripe-Rückkehr
  const ref = useRef(null);

  const load = useCallback(() => {
    axios.get(`${API}/video-studio/wallet`, { withCredentials: true }).then((r) => setWallet(r.data)).catch(() => setWallet(null));
  }, []);

  useEffect(() => {
    axios.get(`${API}/video-studio/studio/flag`, { withCredentials: true }).then((r) => setEnabled(!!r.data?.enabled)).catch(() => setEnabled(false));
  }, []);
  useEffect(() => { if (enabled) load(); }, [enabled, load]);
  useEffect(() => {
    const h = () => load();
    const openH = (ev) => {
      // Mehrere CreditWallet-Instanzen (Sidebar, Mobile-Header, Editor) hören global mit:
      // nur eine SICHTBARE Instanz je Event öffnen (sonst stapeln sich mehrere Panels).
      if (!ref.current || ref.current.offsetParent === null) return;
      if (window.__vsWalletOpenTs === ev.timeStamp) return;
      window.__vsWalletOpenTs = ev.timeStamp;
      setOpen(true);
      load();
    };
    window.addEventListener("wallet-refresh", h);
    window.addEventListener("vs-wallet-open", openH);
    return () => { window.removeEventListener("wallet-refresh", h); window.removeEventListener("vs-wallet-open", openH); };
  }, [load]);

  // Rückkehr aus dem Stripe-Checkout auswerten (?vswallet=ok|abbruch)
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const r = p.get("vswallet");
    if (!r) return undefined;
    const oid = p.get("o");
    // URL säubern, damit ein Reload den Beleg nicht erneut auslöst
    p.delete("vswallet");
    p.delete("o");
    window.history.replaceState({}, "", window.location.pathname + (p.toString() ? `?${p}` : ""));
    if (r === "abbruch") { toast.info("Aufladung abgebrochen – es wurde nichts berechnet."); return undefined; }
    if (r !== "ok") return undefined;
    // Erfolg: Panel öffnen und Beleg laden. Die Gutschrift läuft per Zahlungs-Webhook → bis "credited" pollen.
    setOpen(true);
    setReceipt({ pending: true });
    let tries = 0;
    let timer;
    const poll = () => {
      axios.get(`${API}/video-studio/wallet/last-order${oid ? `?order_id=${encodeURIComponent(oid)}` : ""}`, { withCredentials: true })
        .then((res) => {
          const o = res.data?.order;
          tries += 1;
          if (o && o.status === "credited") {
            setReceipt({ pending: false, credits: o.credits, preis: o.preis, balance: o.balance, invoice_id: o.invoice_id, created_label: o.created_label });
            load();
          } else if (tries < 10) {
            timer = setTimeout(poll, 1500);
          } else {
            setReceipt({ pending: false, slow: true, credits: o?.credits, preis: o?.preis });
            load();
          }
        })
        .catch(() => { tries += 1; if (tries < 10) timer = setTimeout(poll, 1500); else setReceipt(null); });
    };
    poll();
    return () => { if (timer) clearTimeout(timer); };
  }, [load]);

  useEffect(() => {
    if (!open) return undefined;
    if (!packages) axios.get(`${API}/video-studio/wallet/packages`, { withCredentials: true }).then((r) => setPackages(r.data)).catch(() => setPackages({ pakete: [] }));
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, packages]);

  const dismissNotice = () => { setWallet((w) => (w ? { ...w, migration_hinweis: false } : w)); axios.post(`${API}/video-studio/wallet/notice-seen`, {}, { withCredentials: true }).catch(() => {}); };

  const topup = (pkg) => {
    setBuying(pkg.id);
    axios.post(`${API}/video-studio/wallet/topup`, { package_id: pkg.id, origin_url: window.location.origin }, { withCredentials: true })
      .then((r) => {
        if (r.data?.checkout_url) window.location.href = r.data.checkout_url;
        else { toast.info(r.data?.hinweis || "Aufladen gerade nicht möglich."); setBuying(null); }
      })
      .catch((e) => { toast.error(e?.response?.data?.detail || "Aufladen gerade nicht möglich."); setBuying(null); });
  };

  const saveAutoRecharge = (next) => {
    setWallet((w) => ({ ...w, auto_recharge: next }));
    setSavingAR(true);
    axios.post(`${API}/video-studio/wallet/auto-recharge`, next, { withCredentials: true })
      .then((r) => setWallet((w) => ({ ...w, auto_recharge: r.data.auto_recharge })))
      .catch((e) => toast.error(e?.response?.data?.detail || "Einstellung nicht gespeichert."))
      .finally(() => setSavingAR(false));
  };

  if (!enabled || !wallet) return null;
  const bal = wallet.balance;
  const ar = wallet.auto_recharge || { enabled: false, threshold: 20, package_id: "m" };

  return (
    <>
      <button
        type="button"
        ref={ref}
        onClick={() => setOpen((o) => !o)}
        data-testid="wallet-pill"
        aria-label={`Credits: ${bal}`}
        className={`group inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1.5 text-sm font-bold transition-colors ${wallet.low ? "border-[#C9842A] bg-[#FFF3D6] text-[#8A5A16]" : dark ? "border-white/20 bg-white/10 text-[#FDD105] hover:bg-white/15" : "border-[#111] bg-[#111] text-[#FDD105] hover:brightness-110"} ${className}`}
      >
        <Zap size={14} className={wallet.low ? "text-[#C9842A]" : "text-[#FDD105]"} fill="currentColor" />
        <span className="tabular-nums" data-testid="wallet-pill-balance">{bal.toLocaleString("de-DE")}</span>
        <span className="hidden sm:inline text-[11px] font-semibold opacity-80">Credits</span>
      </button>

      {open && createPortal(
        <AnimatePresence>
          <motion.div className="fixed inset-0 z-[400] flex items-center justify-center p-3 sm:block sm:p-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)} data-testid="wallet-overlay">
            <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px]" />
            <motion.div
              initial={{ opacity: 0, y: 12, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.98 }}
              transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              data-testid="wallet-panel"
              data-lenis-prevent
              className={`relative sm:absolute sm:top-[72px] ${align === "right" ? "sm:right-6" : "sm:left-3"} w-[min(100vw-1.5rem,380px)] max-h-[calc(100vh-1.5rem)] sm:max-h-[calc(100vh-88px)] overflow-y-auto rounded-2xl border border-[#E2E2DD] bg-white shadow-[0_24px_60px_-12px_rgba(0,0,0,0.35)]`}
            >
              {/* Guthaben-Kopf */}
              <div className="relative overflow-hidden rounded-t-2xl bg-[#111] px-5 pt-5 pb-6 text-white">
                <span className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-[#FDD105]/20 blur-2xl" aria-hidden="true" />
                <button type="button" onClick={() => setOpen(false)} className="absolute right-3 top-3 rounded-full p-1.5 text-white/60 hover:bg-white/10 hover:text-white" aria-label="Schließen" data-testid="wallet-close"><X size={16} /></button>
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/50">Dein Guthaben</p>
                <p className="mt-1 flex items-end gap-2">
                  <Zap size={26} className="mb-1 text-[#FDD105]" fill="currentColor" />
                  <span className="font-display text-4xl font-black leading-none tabular-nums text-[#FDD105]" data-testid="wallet-balance">{bal.toLocaleString("de-DE")}</span>
                  <span className="mb-1 text-sm font-semibold text-white/60">Credits</span>
                </p>
                <div className="mt-4 grid grid-cols-2 gap-2 text-[12px]">
                  <div className="rounded-lg bg-white/10 px-3 py-2">
                    <p className="text-white/55">Paket diesen Monat</p>
                    <p className="font-bold tabular-nums" data-testid="wallet-package">{wallet.package_credits.toLocaleString("de-DE")}</p>
                    {wallet.renews_label && <p className="mt-0.5 text-[10px] text-white/45">erneuert {wallet.renews_label}</p>}
                  </div>
                  <div className="rounded-lg bg-white/10 px-3 py-2">
                    <p className="text-white/55">Gekauft</p>
                    <p className="font-bold tabular-nums" data-testid="wallet-purchased">{wallet.purchased_credits.toLocaleString("de-DE")}</p>
                    <p className="mt-0.5 text-[10px] text-white/45">verfällt nie</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4 p-4">
                {receipt && (
                  <div className="rounded-xl border border-[#BFE3CB] bg-[#EEF8F1] p-3" data-testid="wallet-receipt">
                    {receipt.pending ? (
                      <p className="flex items-center gap-2 text-[12px] font-semibold text-[#2D6A4F]" data-testid="wallet-receipt-pending">
                        <Loader2 size={15} className="animate-spin shrink-0" /> Zahlung erhalten – dein Guthaben wird gutgeschrieben …
                      </p>
                    ) : (
                      <div data-testid="wallet-receipt-done">
                        <div className="flex items-start justify-between gap-2">
                          <p className="flex items-center gap-1.5 text-[13px] font-bold text-[#2D6A4F]"><ReceiptText size={15} /> Aufladung erfolgreich</p>
                          <button type="button" onClick={() => setReceipt(null)} className="rounded-full p-1 text-[#2D6A4F]/60 hover:bg-[#2D6A4F]/10 hover:text-[#2D6A4F]" aria-label="Beleg schließen" data-testid="wallet-receipt-close"><X size={14} /></button>
                        </div>
                        <div className="mt-2 space-y-1 text-[12px] text-[#2C4A38]">
                          <div className="flex items-center justify-between"><span>Gutgeschrieben</span><span className="flex items-center gap-1 font-bold tabular-nums"><Zap size={12} className="text-[#C9842A]" fill="currentColor" />+{(receipt.credits || 0).toLocaleString("de-DE")} Credits</span></div>
                          <div className="flex items-center justify-between"><span>Betrag</span><span className="font-semibold tabular-nums">{eur(receipt.preis)} <span className="text-[10px] font-normal text-[#6B8575]">inkl. 19 % MwSt.</span></span></div>
                          {!receipt.slow && (
                            <div className="flex items-center justify-between border-t border-[#BFE3CB] pt-1"><span className="font-semibold">Neues Guthaben</span><span className="font-bold tabular-nums" data-testid="wallet-receipt-balance">{(receipt.balance || 0).toLocaleString("de-DE")} Credits</span></div>
                          )}
                        </div>
                        {receipt.slow
                          ? <p className="mt-2 text-[11px] leading-snug text-[#6B8575]">Die Gutschrift erfolgt in wenigen Sekunden durch unser Zahlungssystem – aktualisiere das Guthaben gleich noch einmal.</p>
                          : <p className="mt-2 text-[11px] leading-snug text-[#6B8575]">Deine Rechnung kommt per E-Mail und liegt im Kundenbereich unter „Deine Rechnungen". {receipt.created_label && `· ${receipt.created_label}`}</p>}
                      </div>
                    )}
                  </div>
                )}

                {wallet.migration_hinweis && (
                  <div className="rounded-xl border border-[#E3D3B0] bg-[#F8F4EA] p-3 text-[12px] text-[#5C4A1E]" data-testid="wallet-migration">
                    <p className="flex items-center gap-1.5 font-bold text-[#8A5A16]"><Sparkles size={14} /> Dein bisheriges Guthaben ist da</p>
                    <p className="mt-1 leading-snug">Wir haben dein bisheriges Video-Guthaben fair in Credit-Punkte umgewandelt – zu deinen Gunsten. Nichts verfällt.</p>
                    <button type="button" onClick={dismissNotice} className="mt-2 inline-flex items-center gap-1 rounded-lg bg-[#111] px-3 py-1.5 text-[11px] font-bold text-[#FDD105]" data-testid="wallet-migration-ok"><Check size={12} /> Verstanden</button>
                  </div>
                )}

                {wallet.low && (
                  <div className="flex items-start gap-2 rounded-xl border border-[#C9842A]/40 bg-[#FFF3D6] p-3 text-[12px] text-[#8A5A16]" data-testid="wallet-low">
                    <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                    <p>Dein Guthaben wird knapp. Lade auf, damit du jederzeit weiter schneiden und den Film herunterladen kannst.</p>
                  </div>
                )}

                {/* Aufladen */}
                <div>
                  <p className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#8C867B]"><Plus size={13} /> Aufladen</p>
                  <div className="grid grid-cols-3 gap-2" data-testid="wallet-packages">
                    {(packages?.pakete || []).map((p) => (
                      <button key={p.id} type="button" onClick={() => topup(p)} disabled={!!buying} data-testid={`wallet-package-${p.id}`}
                        className="relative rounded-xl border border-[#E2E2DD] bg-[#FBFAF5] px-2 py-3 text-center transition-colors hover:border-[#111] hover:bg-white disabled:opacity-60">
                        {p.badge && <span className="absolute -top-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-[#FDD105] px-2 py-0.5 text-[9px] font-bold text-[#111]">{p.badge}</span>}
                        {buying === p.id ? (
                          <Loader2 size={18} className="mx-auto my-1 animate-spin text-[#111]" />
                        ) : (
                          <>
                            <p className="flex items-center justify-center gap-0.5 font-display text-base font-black tabular-nums text-[#111]"><Zap size={12} className="text-[#C9842A]" fill="currentColor" />{p.credits}</p>
                            <p className="mt-0.5 text-[11px] font-semibold text-[#5C574F]">{eur(p.preis)}</p>
                          </>
                        )}
                      </button>
                    ))}
                    {packages && (packages.pakete || []).length === 0 && <p className="col-span-3 text-xs text-[#8C867B]">Keine Pakete hinterlegt.</p>}
                  </div>
                  <p className="mt-1.5 flex items-center gap-1 text-[10px] leading-snug text-[#8C867B]" data-testid="wallet-topup-note"><CreditCard size={11} className="shrink-0" /> Sichere Zahlung per Karte · EUR inkl. 19 % MwSt. · dein Guthaben verfällt nicht.</p>
                </div>

                {/* Automatisch aufladen */}
                <div className="rounded-xl border border-[#ECECE8] bg-[#FBFAF5] p-3">
                  <label className="flex items-center justify-between gap-2">
                    <span className="text-[12px] font-semibold text-[#111]">Automatisch aufladen</span>
                    <button type="button" role="switch" aria-checked={ar.enabled} disabled={savingAR}
                      onClick={() => { if (ar.enabled) saveAutoRecharge({ ...ar, enabled: false, consent: false }); else setArConsent((v) => !v); }}
                      data-testid="wallet-autorecharge-toggle"
                      className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${ar.enabled ? "bg-[#111]" : "bg-[#D4D0C6]"}`}>
                      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${ar.enabled ? "translate-x-4" : "translate-x-0.5"}`} />
                    </button>
                  </label>
                  {!ar.enabled && arConsent && (
                    <div className="mt-2 space-y-2 rounded-lg border border-[#E3D3B0] bg-white p-2.5 text-[11px] text-[#5C4A1E]" data-testid="wallet-autorecharge-consent">
                      <p className="leading-snug">Wenn dein Guthaben unter die Schwelle fällt, laden wir das Paket <strong>{(packages?.pakete || []).find((p) => p.id === ar.package_id)?.credits || ""} Credits</strong> automatisch über deine beim nächsten Kauf hinterlegte Karte auf. Du kannst das jederzeit wieder ausschalten.</p>
                      <button type="button" disabled={savingAR} onClick={() => { setArConsent(false); saveAutoRecharge({ ...ar, enabled: true, consent: true }); }} data-testid="wallet-autorecharge-confirm"
                        className="inline-flex items-center gap-1 rounded-lg bg-[#111] px-3 py-1.5 text-[11px] font-bold text-[#FDD105]"><Check size={12} /> Einverstanden – aktivieren</button>
                    </div>
                  )}
                  {ar.enabled && (
                    <div className="mt-2 flex items-center gap-2 text-[11px] text-[#5C574F]" data-testid="wallet-autorecharge-config">
                      <span>wenn unter</span>
                      <input type="number" min="0" value={ar.threshold} onChange={(e) => setWallet((w) => ({ ...w, auto_recharge: { ...ar, threshold: Number(e.target.value) } }))} onBlur={() => saveAutoRecharge(ar)} className="w-16 rounded-md border border-[#D4D0C6] px-2 py-1 tabular-nums" data-testid="wallet-autorecharge-threshold" />
                      <span>Credits</span>
                    </div>
                  )}
                </div>

                {/* Verlauf */}
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#8C867B]"><Clock size={13} /> Verlauf</p>
                    {(wallet.history || []).length > 0 && <button type="button" onClick={() => { setOpen(false); navigate("/studio-guthaben"); }} className="text-[11px] font-semibold text-[#B88E3E] underline underline-offset-2" data-testid="wallet-history-all">Alle anzeigen</button>}
                  </div>
                  <ul className="space-y-1.5" data-testid="wallet-history">
                    {(wallet.history || []).slice(0, 5).map((h) => {
                      const Icon = KIND_ICON[h.kind] || Clock;
                      const pos = h.delta >= 0;
                      return (
                        <li key={h.id} className="flex items-center gap-2.5 rounded-lg border border-[#F0EFEA] px-2.5 py-2" data-testid={`wallet-history-${h.id}`}>
                          <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-full ${pos ? "bg-[#E8F3EC] text-[#2D6A4F]" : "bg-[#FBEAEA] text-[#9B2C2C]"}`}><Icon size={13} /></span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-[12px] font-medium text-[#111]">{h.label}</span>
                            {h.at_label && <span className="block text-[10px] text-[#8C867B]">{h.at_label}</span>}
                          </span>
                          <span className={`shrink-0 text-[12px] font-bold tabular-nums ${pos ? "text-[#2D6A4F]" : "text-[#9B2C2C]"}`}>{pos ? "+" : ""}{h.delta}</span>
                        </li>
                      );
                    })}
                    {(wallet.history || []).length === 0 && <li className="py-3 text-center text-xs text-[#8C867B]" data-testid="wallet-history-empty">Noch keine Bewegungen.</li>}
                  </ul>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default CreditWallet;
